import React, { useState, useEffect } from "react";
import { Button, Input, message, Upload, Spin } from "antd";
import { PlusOutlined } from "@ant-design/icons";
import axios from "axios";
import { useNavigate, useLocation } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";

const PageRegister = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const [avatarFile, setAvatarFile] = useState(null);
  const [avatarPreview, setAvatarPreview] = useState(null);
  const [initialEmail, setInitialEmail] = useState("");
  const [loadingToken, setLoadingToken] = useState(true);
  const [registerToken, setRegisterToken] = useState("");

  /* =========================================================
     VÉRIFICATION DU TOKEN D’INSCRIPTION
     ========================================================= */
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const token = params.get("token");

    if (!token) {
      message.error("Lien d'inscription invalide !");
      navigate("/");
      return;
    }

    setRegisterToken(token); // stocke le token

    axios
      .get(`${process.env.REACT_APP_API_URL}/api/auth/verify-token/${token}`)
      .then((res) => {
        if (res.data.valid) {
          setInitialEmail(res.data.email);
        } else {
          message.error("Lien expiré ou invalide !");
          navigate("/");
        }
      })
      .catch((err) => {
        console.error(err);
        message.error("Erreur lors de la validation du token !");
        navigate("/");
      })
      .finally(() => setLoadingToken(false));
  }, [location, navigate]);

  /* =========================================================
     VALIDATION FORMULAIRE
     ========================================================= */
  const registerSchema = Yup.object().shape({
    name: Yup.string().required("Nom requis"),
    password: Yup.string()
      .min(6, "Minimum 6 caractères")
      .required("Mot de passe requis"),
  });

  /* =========================================================
     GESTION AVATAR
     ========================================================= */
  const handleAvatarChange = (info) => {
    const file = info.file.originFileObj;
    if (file) {
      setAvatarFile(file);
      const reader = new FileReader();
      reader.onload = (e) => setAvatarPreview(e.target.result);
      reader.readAsDataURL(file);
    }
  };

  /* =========================================================
     SOUMISSION INSCRIPTION
     ========================================================= */
  const handleRegister = async (values, setSubmitting, setErrors) => {
    const formData = new FormData();
    formData.append("name", values.name);
    formData.append("email", initialEmail);
    formData.append("password", values.password);
    formData.append("token", registerToken);
    if (avatarFile) formData.append("avatar", avatarFile);

    try {
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/auth/register`,
        formData
      );
      message.success("Enregistrement réussi !");
      navigate("/login");
    } catch (err) {
      const errorMessage =
        err.response?.data?.message || "Échec de l'enregistrement !";
      message.error(errorMessage);
      setErrors({ general: errorMessage });
    } finally {
      setSubmitting(false);
    }
  };

  if (loadingToken) {
    return <Spin size="large" style={{ marginTop: 100 }} />;
  }

  /* =========================================================
     RENDER
     ========================================================= */
  return (
    <div style={{ display: "flex", justifyContent: "center", paddingTop: 50 }}>
      <Formik
        initialValues={{ name: "", password: "" }}
        validationSchema={registerSchema}
        onSubmit={(values, { setSubmitting, setErrors }) =>
          handleRegister(values, setSubmitting, setErrors)
        }
      >
        {({ errors, touched, isSubmitting }) => (
          <Form
            style={{
              width: 400,
              background: "#fff",
              padding: 20,
              borderRadius: 8,
            }}
          >
            <div>
              <label>Nom</label>
              <Field as={Input} name="name" />
              {touched.name && errors.name && (
                <div style={{ color: "red" }}>{errors.name}</div>
              )}
            </div>

            <div style={{ marginTop: 16 }}>
              <label>Email</label>
              <Input value={initialEmail} disabled />
            </div>

            <div style={{ marginTop: 16 }}>
              <label>Mot de passe</label>
              <Field as={Input.Password} name="password" />
              {touched.password && errors.password && (
                <div style={{ color: "red" }}>{errors.password}</div>
              )}
            </div>

            <div style={{ marginTop: 16 }}>
              <label>Avatar</label>
              <Upload
                name="avatar"
                listType="picture-circle"
                showUploadList={false}
                beforeUpload={() => true} // ← permet l'aperçu
                onChange={handleAvatarChange}
              >
                {avatarPreview ? (
                  <img
                    src={avatarPreview}
                    alt="avatar"
                    style={{ width: "100%", borderRadius: "50%" }}
                  />
                ) : (
                  <div>
                    <PlusOutlined />
                    <div style={{ marginTop: 8 }}>Envoyer</div>
                  </div>
                )}
              </Upload>
            </div>

            <Button
              type="primary"
              htmlType="submit"
              block
              loading={isSubmitting}
              style={{ marginTop: 16 }}
            >
              S'inscrire
            </Button>
          </Form>
        )}
      </Formik>
    </div>
  );
};

export default PageRegister;
